import { useRef, useState } from 'react';
import { RiCheckLine, RiCloseLine, RiEyeLine, RiEyeOffLine, RiFileCopyLine, RiRefreshLine, RiSearchLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { useFieldControlProps } from './field';
import { IconButton } from './icon-button';
import { renderIcon, type IconLike } from './icon';
import { mergeRefs, setNativeValue } from './lib/refs';
import { tv } from './lib/tv';

/**
 * Product field box (spec §4.5): h32 (h36 in modal A), r8, `bg-field` (White/10), no border, padding 0/12, gap 6,
 * value `t-field` (400 13/18) white, placeholder White/60. Focus: 1px inside border `#35b5ec`.
 */
export const fieldBoxStyles = tv({
  slots: {
    box: [
      'group/field relative flex w-full min-w-0 items-center rounded-8 border border-transparent bg-field text-fg',
      'transition-[border-color,background-color] duration-150 ease-standard',
      'focus-within:border-focus has-[:disabled]:cursor-not-allowed has-[:disabled]:text-fg-tertiary',
    ],
    input: [
      'h-full min-w-0 flex-1 bg-transparent text-inherit outline-none placeholder:text-fg-secondary',
      'disabled:cursor-not-allowed [&::-webkit-search-cancel-button]:appearance-none',
    ],
    icon: 'shrink-0 text-icon',
  },
  variants: {
    size: {
      sm: { box: 'h-24 gap-4 px-6', icon: 'size-16' },
      md: { box: 'h-32 gap-6 px-12', icon: 'size-16' },
      lg: { box: 'h-36 gap-6 px-12', icon: 'size-16' },
    },
    /** `field` — values 400 13/18 (`t-field`); `search` — 400 12/17 (577:14377), header 400 12/16 White/80. */
    kind: { field: '', search: '' },
    invalid: { true: { box: 'border-tone-red-50 focus-within:border-tone-red' } },
  },
  compoundVariants: [
    { kind: 'field', size: ['md', 'lg'], class: { input: 't-field' } },
    { kind: 'field', size: 'sm', class: { input: 'text-xs tracking-normal' } },
    { kind: 'search', size: ['md', 'lg'], class: { box: 'gap-8', input: 'text-xs leading-17 tracking-normal' } },
    { kind: 'search', size: 'sm', class: { input: 'text-xs tracking-normal text-white-800 light:text-black-800' } },
  ],
  defaultVariants: { size: 'md', kind: 'field' },
});

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  ref?: React.Ref<HTMLInputElement>;
  /** `sm` 24 (header) · `md` 32 (panels, default) · `lg` 36 (modal A). */
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: IconLike;
  /** Content inside the box on the right (unit "px", Kbd, status…). */
  rightSlot?: React.ReactNode;
  /** Buttons outside the box, gap 8 (login copy / refresh, 501:11647). */
  actions?: React.ReactNode;
  /** Show a clear (×) button while the field has a value. */
  clearable?: boolean;
  onClear?: () => void;
  /** Accessible label of the clear button. */
  clearLabel?: string;
  invalid?: boolean;
  /** Class of the outer wrapper (box + actions). */
  className?: string;
  /** Class of the <input> element. */
  inputClassName?: string;
  /** Text style of the value (internal: SearchInput uses `search`). */
  kind?: 'field' | 'search';
}

export function Input({
  ref,
  size,
  leftIcon,
  rightSlot,
  actions,
  clearable,
  onClear,
  clearLabel = 'Tozalash',
  invalid,
  className,
  inputClassName,
  value,
  defaultValue,
  onChange,
  disabled,
  required,
  id,
  'aria-describedby': ariaDescribedBy,
  kind,
  ...rest
}: InputProps) {
  const field = useFieldControlProps({ id, disabled, required, invalid, 'aria-describedby': ariaDescribedBy });
  const inputRef = useRef<HTMLInputElement>(null);
  const [uncontrolledFilled, setUncontrolledFilled] = useState(() => defaultValue != null && String(defaultValue) !== '');
  const filled = value !== undefined ? value != null && String(value) !== '' : uncontrolledFilled;
  const s = fieldBoxStyles({ size, kind, invalid: field.invalid });

  const clear = () => {
    const input = inputRef.current;
    if (input) {
      setNativeValue(input, '');
      input.focus();
    }
    onClear?.();
  };

  const box = (
    <div className={s.box({ class: actions ? undefined : className })} data-invalid={field.invalid || undefined}>
      {renderIcon(leftIcon, s.icon())}
      <input
        {...rest}
        ref={mergeRefs(inputRef, ref)}
        id={field.id}
        value={value}
        defaultValue={defaultValue}
        disabled={field.disabled}
        required={field.required}
        aria-invalid={field.invalid || undefined}
        aria-describedby={field['aria-describedby']}
        onChange={(e) => {
          if (value === undefined) setUncontrolledFilled(e.currentTarget.value !== '');
          onChange?.(e);
        }}
        className={s.input({ class: inputClassName })}
      />
      {clearable && filled && !field.disabled && !rest.readOnly && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={clearLabel}
          onClick={clear}
          className="-mr-4 flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-4 text-icon hover:bg-hover hover:text-fg"
        >
          <RiCloseLine aria-hidden className="size-16" />
        </button>
      )}
      {rightSlot}
    </div>
  );

  if (!actions) return box;
  return (
    <div className={cn('flex w-full min-w-0 items-center gap-8', className)}>
      {box}
      {actions}
    </div>
  );
}

/** Square 32 r8 White/5 action next to a field (copy / refresh / eye, 501:10685). */
export function InputAction({ className, ...props }: Omit<React.ComponentProps<typeof IconButton>, 'size' | 'variant'>) {
  // icons are white here (514:11333), unlike the White/60 default of IconButton
  return <IconButton size="md" variant="secondary" className={cn('text-fg', className)} {...props} />;
}

export interface PasswordInputProps extends Omit<InputProps, 'type' | 'actions'> {
  /** Eye button to show / hide the value. Default true. */
  revealable?: boolean;
  /** Copy button (copies the current value to the clipboard). */
  copyable?: boolean;
  /** Called after a successful copy. */
  onCopied?: (value: string) => void;
  /** "Regenerate" button — the handler should set a new value. */
  onRegenerate?: () => void;
  labels?: { show?: string; hide?: string; copy?: string; copied?: string; regenerate?: string };
  /** Extra buttons after the built-in ones. */
  actions?: React.ReactNode;
}

/** Password field with show / copy / regenerate buttons (501:11660 "PAROL"). */
export function PasswordInput({
  ref,
  revealable = true,
  copyable,
  onCopied,
  onRegenerate,
  labels,
  actions,
  autoComplete = 'new-password',
  ...rest
}: PasswordInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const l = {
    show: "Parolni ko'rsatish",
    hide: 'Parolni yashirish',
    copy: 'Nusxa olish',
    copied: 'Nusxa olindi',
    regenerate: 'Yangi parol yaratish',
    ...labels,
  };

  const copy = async () => {
    const text = inputRef.current?.value ?? '';
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onCopied?.(text);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — nothing to do */
    }
  };

  const buttons =
    revealable || copyable || onRegenerate || actions ? (
      <>
        {revealable && (
          <InputAction
            icon={visible ? RiEyeOffLine : RiEyeLine}
            aria-label={visible ? l.hide : l.show}
            aria-pressed={visible}
            onClick={() => setVisible((v) => !v)}
            disabled={rest.disabled}
          />
        )}
        {copyable && (
          <InputAction
            icon={copied ? RiCheckLine : RiFileCopyLine}
            aria-label={copied ? l.copied : l.copy}
            onClick={copy}
            disabled={rest.disabled}
          />
        )}
        {onRegenerate && <InputAction icon={RiRefreshLine} aria-label={l.regenerate} onClick={onRegenerate} disabled={rest.disabled} />}
        {actions}
      </>
    ) : undefined;

  return (
    <Input
      {...rest}
      ref={mergeRefs(inputRef, ref)}
      type={visible ? 'text' : 'password'}
      autoComplete={autoComplete}
      spellCheck={false}
      actions={buttons}
    />
  );
}

export interface SearchInputProps extends Omit<InputProps, 'type' | 'leftIcon'> {
  /** Accessible name when there is no visible label. Default "Qidirish". */
  'aria-label'?: string;
}

/**
 * Search field — toolbar/panel `md` (h32, gap 8, search-line 16, 400 12/17, 577:14377) and header `sm`
 * (h24, padding 4/6, 400 12/16 White/80, w201). Clearable by default.
 */
export function SearchInput({ size = 'md', clearable = true, ...rest }: SearchInputProps) {
  return (
    <Input
      aria-label="Qidirish"
      {...rest}
      type="search"
      kind="search"
      size={size}
      clearable={clearable}
      leftIcon={RiSearchLine}
    />
  );
}
